import { motion } from "framer-motion";
import { Check, Mail } from "lucide-react";
import { signupConfirmationContent, planDisplayNames } from "@/data/signupConfirmationContent";
import { SUBSCRIPTION_TIERS, type SubscriptionTier } from "@/lib/subscription";

interface SignupConfirmationProps {
  email: string;
  plan: 'free' | 'starter' | 'pro' | 'unlimited';
  billing?: 'monthly' | 'annual';
}

const SignupConfirmation = ({ email, plan, billing = 'monthly' }: SignupConfirmationProps) => {
  const isPaidPlan = plan !== 'free';
  const content = isPaidPlan 
    ? signupConfirmationContent.paid 
    : signupConfirmationContent.free;
  
  const planName = isPaidPlan ? planDisplayNames[plan] || plan : '';
  let subtitle = content.subtitle.replace('{planName}', planName);
  
  if (isPaidPlan) {
    const config = SUBSCRIPTION_TIERS[plan as SubscriptionTier];
    const isAnnual = billing === 'annual';
    const displayPrice = isAnnual ? config.annualMonthly : config.monthlyPrice;
    const billingSuffix = isAnnual ? '/mo billed annually' : '/month';
    subtitle = `${subtitle} at $${displayPrice}${billingSuffix}`;
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className="bg-card border border-border rounded-2xl p-8 shadow-sm"
    >
      {/* Success Icon */}
      <div className="flex justify-center mb-6">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.1, type: "spring", stiffness: 200, damping: 15 }}
          className="relative"
        >
          <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 15 }}
              className="w-12 h-12 rounded-full bg-accent flex items-center justify-center"
            >
              <Check className="h-6 w-6 text-accent-foreground" strokeWidth={3} />
            </motion.div>
          </div>
          {/* Pulse ring animation */}
          <motion.div
            initial={{ opacity: 0.6, scale: 1 }}
            animate={{ opacity: 0, scale: 1.5 }}
            transition={{ delay: 0.3, duration: 0.8, ease: "easeOut" }}
            className="absolute inset-0 w-16 h-16 rounded-full bg-accent/20"
          />
        </motion.div>
      </div>

      {/* Title and Subtitle */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="text-center mb-8"
      >
        <h1 className="font-display text-2xl font-bold text-foreground mb-2">
          {content.title}
        </h1>
        <p className="text-muted-foreground">
          {subtitle}
        </p>
      </motion.div>

      {/* Steps */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="space-y-3 mb-8"
      >
        {content.steps.map((step, index) => (
          <motion.div
            key={step.number}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 + index * 0.1 }}
            className="flex items-start gap-4 py-3"
          >
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-link/10 flex items-center justify-center">
              <span className="text-sm font-semibold text-link">
                {step.number}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-foreground">
                {step.title}
              </h3>
              <p className="text-sm text-muted-foreground">
                {step.description}
              </p>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Email sent to */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="text-center space-y-3"
      >
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Mail className="h-4 w-4" />
          <span>We sent a confirmation email to:</span>
        </div>
        <p className="font-medium text-foreground break-all">
          {email}
        </p>
        <p className="text-xs text-muted-foreground pt-2">
          {content.emailNote}
        </p>
      </motion.div>
    </motion.div>
  );
};

export default SignupConfirmation;
